import { z } from "zod";
import { CANONICAL_RESULT_SCHEMA_VERSION, canonicalResultJson } from "./canonical-result.js";
import { twinExecutionCertificateDraftV0Schema } from "./twin-execution-certificates.js";
const id = z.string().trim().min(1).max(200);
const externalId = z.string().trim().min(1).max(1_000);
const limit = z.number().int().min(1).max(20).default(10);
const commonProcessFilters = {
    definitionId: id.optional(),
    definitionSlug: id.optional(),
    definitionVersion: z.number().int().positive().optional(),
    instanceId: id.optional(),
};
export const candidateTwinQueryInputSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("relationship_definitions"),
        ruleId: id.optional(),
        ruleSlug: id.optional(),
        ruleVersion: z.number().int().positive().optional(),
        source: id.optional(),
        entity: id.optional(),
        predicate: id.optional(),
        limit,
    }).strict(),
    z.object({
        kind: z.literal("relationship_observations"),
        ruleId: id.optional(),
        ruleSlug: id.optional(),
        ruleVersion: z.number().int().positive().optional(),
        subjectEntityId: id.optional(),
        subjectExternalId: externalId.optional(),
        subjectDisplayName: id.optional(),
        predicate: id.optional(),
        objectEntityId: id.optional(),
        objectExternalId: externalId.optional(),
        objectDisplayName: id.optional(),
        source: id.optional(),
        entity: id.optional(),
        externalId: externalId.optional(),
        limit,
    }).strict(),
    z.object({
        kind: z.literal("process_definitions"),
        definitionId: id.optional(),
        definitionSlug: id.optional(),
        definitionVersion: z.number().int().positive().optional(),
        limit,
    }).strict(),
    z.object({
        kind: z.literal("process_instances"),
        ...commonProcessFilters,
        anchorEntityId: id.optional(),
        currentState: id.optional(),
        limit,
    }).strict(),
    z.object({
        kind: z.literal("process_instance_summary"),
        definitionId: id.optional(),
        definitionSlug: id.optional(),
        definitionVersion: z.number().int().positive().optional(),
        limit,
    }).strict(),
    z.object({
        kind: z.literal("process_observations"),
        ...commonProcessFilters,
        observedState: id.optional(),
        source: id.optional(),
        entity: id.optional(),
        externalId: externalId.optional(),
        limit,
    }).strict(),
    z.object({
        kind: z.literal("process_transitions"),
        ...commonProcessFilters,
        fromState: id.optional(),
        toState: id.optional(),
        limit,
    }).strict(),
]).superRefine((query, context) => {
    if ((query.kind === "relationship_definitions" || query.kind === "relationship_observations"
        || query.kind === "process_observations")
        && (query.source === undefined) !== (query.entity === undefined)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: [query.source === undefined ? "source" : "entity"],
            message: "Candidate source and entity filters must be provided together." });
    }
});
export const candidateTwinQueryRequestSchema = z.object({
    workerId: id,
    attemptNumber: z.number().int().positive(),
    tenantSlug: id,
    organisationId: id,
    certificateVersion: z.literal("mlx.execution-certificate.v0").optional(),
    toolCallId: id.optional(),
    query: candidateTwinQueryInputSchema,
}).strict().superRefine((request, context) => {
    if ((request.certificateVersion === undefined) !== (request.toolCallId === undefined)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["certificateVersion"],
            message: "Candidate certificate version and tool call id must be requested together." });
    }
});
const sha256 = z.string().regex(/^[0-9a-f]{64}$/u);
const evidenceCutId = z.string().regex(/^ecut_[0-9a-f]{64}$/u);
export const candidateTwinQueryIdentitySchema = z.object({
    runId: id,
    tenantSlug: id,
    organisationId: id,
    buildId: id,
    catalogSha256: sha256,
    evidenceCutId,
    evidenceCutManifestSha256: sha256,
    candidateReleaseSha: z.string().regex(/^[0-9a-f]{7,64}$/u),
}).strict();
export const candidateTwinQueryResponseSchema = z.object({
    provenance: candidateTwinQueryIdentitySchema,
    kind: z.enum(["relationship_definitions", "relationship_observations", "process_definitions",
        "process_instances", "process_instance_summary", "process_observations", "process_transitions"]),
    rows: z.array(z.record(z.string(), z.unknown())).max(20),
    execution: z.object({
        sql: z.string().min(1).max(200_000),
        queriedTables: z.array(z.string().trim().min(1).max(300)).min(1).max(32),
        engineVersion: z.string().trim().min(1).max(200),
    }).strict().optional(),
    certificateDraft: twinExecutionCertificateDraftV0Schema.optional(),
}).strict();
function stableJson(value) {
    if (Array.isArray(value))
        return `[${value.map(stableJson).join(",")}]`;
    if (value && typeof value === "object")
        return `{${Object.entries(value)
            .sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0)
            .map(([key, item]) => `${JSON.stringify(key)}:${stableJson(item)}`).join(",")}}`;
    return JSON.stringify(value) ?? JSON.stringify(String(value));
}
export function candidateTwinCanonicalResultJson(rows) {
    const canonicalRows = rows.map(stableJson).sort().map((row, index) => ({
        row: { kind: "string", value: row },
        __mlx_candidate_ordinal: { kind: "integer", value: String(index) },
    }));
    return canonicalResultJson({ schemaVersion: CANONICAL_RESULT_SCHEMA_VERSION,
        columns: ["row", "__mlx_candidate_ordinal"], stableKey: ["__mlx_candidate_ordinal"],
        rows: canonicalRows });
}
